import { DialogActions, DialogContent } from "@mui/material";
import React, { useState } from "react";

import BNButton from "../FormElements/BNButton/BNButton";
import CheckIcon from "@mui/icons-material/Check";
import CookieConsentDialogDetails from "./CookieConsentDialogDetails";
import { RegionId } from "../../Contract/RegionId";
import BNDialog from "../BNDialog/BNDialog";
import BNBox from "../BNBox/BNBox";
import { useLocation } from "react-router";

const LINK_PLACEHOLDER = "{link}";

declare global {
  interface Window {
    dataLayer: any;
  }
}
export interface ICookieConsent {
  hasConsented: boolean;
  wantsAnalysis: boolean;
  wantsMarketing: boolean;
  wantsMarketingIsRedacted: boolean;
  wantsPersonalization: boolean;
  isEnabled: boolean;
  consentedDateTime: string;
}
export interface ICookieConsentDialogStrings {
  cookieConsentDialog_ButtonAcceptAll: string;
  cookieConsentDialog_ButtonChange: string;
  cookieConsentDialog_ButtonChangeSmall: string;
  cookieConsentDialog_ButtonStoreChanges: string;
  cookieConsentDialog_Header: string;

  cookieConsentDialog_HeaderChange: string;
  cookieConsentDialog_HeaderNecessary: string;
  cookieConsentDialog_HeaderAnalysis: string;
  cookieConsentDialog_HeaderMarketing: string;
  cookieConsentDialog_HeaderPersonalization: string;

  cookieConsentDialog_Info: string;
  cookieConsentDialog_InfoAnalysis: string;
  cookieConsentDialog_InfoMarketing: string;
  cookieConsentDialog_InfoNecessary: string;
  cookieConsentDialog_InfoPersonalization: string;

  cookieConsentDialog_ReadMore: string;
  cookieConsentDialog_ReadMoreLink: string;
  cookieConsentDialog_PrivacyPolicyLink: string;
  cookieConsentDialog_SwitchAlwaysOn: string;
}
interface Props {
  consent: ICookieConsent;
  strings: ICookieConsentDialogStrings;
  approve: (analysis: boolean, marketing: boolean, personalization: boolean) => void;
  open: boolean;
  region: RegionId;
  disableCookieConsentForMarketingAndPersonalisation: boolean;
}

const renderTextWithLink = (text: string | undefined, linkLabel: string | undefined, href: string, dataUit: string) => {
  const [before, after] = (text ?? "").split(LINK_PLACEHOLDER);
  if (after === undefined) {
    return <>{text}</>;
  }
  return (
    <>
      {before}
      <BNButton
        href={href}
        target="_blank"
        variant="text"
        data-uit={dataUit}
        sx={{
          p: 0,
        }}
      >
        {linkLabel}
      </BNButton>
      {after}
    </>
  );
};

export const CookieConsentDialog: React.FC<Props> = (props) => {
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const handleClose = () => {};
  const location = useLocation();
  const localePrefix = location.pathname.startsWith("/sv/") ? "/sv" : "";

  const showDetails = () => {
    setDetailsOpen(true);
  };
  return (
    <BNDialog onClose={handleClose} open={props.open} important showCloseButton={false} preventCloseOnDismiss>
      {!detailsOpen && (
        <>
          <DialogContent sx={{ maxWidth: "48.5rem", m: { md: 1 } }}>
            <BNBox
              component={"h3"}
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              {props?.strings?.cookieConsentDialog_Header}
            </BNBox>
            <BNBox sx={{ pb: 3 }}>
              <BNBox>
                {renderTextWithLink(
                  props?.strings?.cookieConsentDialog_Info,
                  props?.strings?.cookieConsentDialog_PrivacyPolicyLink,
                  `${localePrefix}/privacypolicy/`,
                  "privacy-policy-link",
                )}
              </BNBox>
              <BNBox>
                {renderTextWithLink(
                  props?.strings?.cookieConsentDialog_ReadMore,
                  props?.strings?.cookieConsentDialog_ReadMoreLink,
                  `${localePrefix}/cookie-policy/`,
                  "cookie-policy-link",
                )}
              </BNBox>
            </BNBox>
          </DialogContent>
          <DialogActions
            sx={{
              mx: 2,
              mb: 4,
            }}
          >
            <BNBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <BNButton variant="outlined" data-uit="show-details" onClick={showDetails}>
                <BNBox component={"span"}>{props?.strings?.cookieConsentDialog_ButtonChange}</BNBox>
              </BNButton>
              <BNButton
                startIcon={<CheckIcon />}
                id="approveAllCookies"
                data-uit="accept-all"
                onClick={() => {
                  props.approve(true, true, true);
                }}
                color="success"
              >
                {props?.strings?.cookieConsentDialog_ButtonAcceptAll}
              </BNButton>
            </BNBox>
          </DialogActions>
        </>
      )}
      {detailsOpen && (
        <>
          <CookieConsentDialogDetails
            store={props.approve}
            strings={props.strings}
            region={props.region}
            consent={props.consent}
            disableCookieConsentForMarketingAndPersonalisation={
              props.disableCookieConsentForMarketingAndPersonalisation
            }
          />
        </>
      )}
    </BNDialog>
  );
};

export default CookieConsentDialog;
